import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChangeEvent } from 'react';
import { Search, FileText, Eye, Plus } from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Button } from '../../components/Shared/Button';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { useUrlListState } from '../../hooks/useUrlListState';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../components/Shared/Table';
import { Badge } from '../../components/Shared/Badge';

import { useOfferStore } from '../../store/useOfferStore';
import { useCustomerStore } from '../../store/useCustomerStore';
import { formatContactPerson, toSalutation } from '../../utils/salutation';
import { formatMoney } from '../../utils/money';

const OFFER_STATUS_FILTERS = ['all', 'draft', 'sent', 'accepted', 'rejected', 'expired'] as const;
type OfferStatusFilter = (typeof OFFER_STATUS_FILTERS)[number];

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

export default function OffersPage() {
  const { t } = useTranslation(['offers', 'common']);
  const navigate = useNavigate();

  const {
    offers, loading, error, fetchOffers, lastQuery,
    totalCount, page, pageSize,
    customerNames, objectNames, loadNameLookups,
  } = useOfferStore();

  const { customers, fetchCustomers } = useCustomerStore();

  const [listState, setListState] = useUrlListState(
    { q: '', customer: 'all', status: 'all' as OfferStatusFilter, page: 1 },
    { status: OFFER_STATUS_FILTERS },
  );
  const { q: searchTerm, customer: filterCustomerId, status: filterStatus, page: activePage } = listState;
  // why: the store still holds the query of the last visit, which can differ from this URL;
  // render only once this list's own fetch has finished.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const lastFilterKey = useRef<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetchCustomers({ pageSize: 200 }),
      loadNameLookups(),
    ]);
  }, [fetchCustomers, loadNameLookups]);

  useEffect(() => {
    // Debounce typing and filter changes only; the first load and page clicks fetch at once.
    const filterKey = [searchTerm, filterCustomerId, filterStatus].join('|');
    const delay = lastFilterKey.current === null || lastFilterKey.current === filterKey ? 0 : 350;
    lastFilterKey.current = filterKey;

    const handle = window.setTimeout(() => {
      fetchOffers({
        page: activePage,
        pageSize: DEFAULT_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
        customerId: filterCustomerId !== 'all'
          ? filterCustomerId : undefined,
        status: filterStatus !== 'all'
          ? filterStatus : undefined,
      }).then(() => setInitialLoadComplete(true));
    }, delay);

    return () => window.clearTimeout(handle);
  }, [searchTerm, filterCustomerId, filterStatus, activePage, fetchOffers]);

  const pageCount = useMemo(() => {
    const safePageSize = pageSize || DEFAULT_PAGE_SIZE;
    return Math.max(1, Math.ceil((totalCount || 0) / safePageSize));
  }, [totalCount, pageSize]);

  const goToPage = (nextPage: number) =>
    setListState({ page: Math.max(1, Math.min(pageCount, nextPage)) });

  const getStatusBadge = (status?: string | null) => {
    const s = (status ?? '').toLowerCase();
    switch (s) {
      case 'draft':
        return <Badge variant="default">{t('offers:status.draft')}</Badge>;
      case 'sent':
        return <Badge variant="info">{t('offers:status.sent')}</Badge>;
      case 'accepted':
        return <Badge variant="success">{t('offers:status.accepted')}</Badge>;
      case 'rejected':
        return <Badge variant="danger">{t('offers:status.rejected')}</Badge>;
      case 'expired':
        return <Badge variant="warning">{t('offers:status.expired')}</Badge>;
      default:
        return <Badge variant="default">{status || '\u2014'}</Badge>;
    }
  };

  const navigateToOffer = (id?: string | null) => {
    if (id) navigate(`/offers/${id}`);
  };

  if (!initialLoadComplete) {
    return <Loading />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('offers:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('offers:subtitle')}
            </p>
          </div>
          <Button
            onClick={() => navigate('/offers/new')}
            leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            {t('offers:actions.create')}
          </Button>
        </div>
      </div>

      <FiltersCard
        searchTerm={searchTerm}
        onSearchChange={(e) => setListState({ q: e.target.value })}
        filterCustomerId={filterCustomerId}
        onCustomerChange={(e) => setListState({ customer: e.target.value })}
        filterStatus={filterStatus}
        onStatusChange={(e) =>
          setListState({ status: e.target.value as OfferStatusFilter })
        }
        customers={customers}
        t={t}
      />

      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('offers:messages.loadError')}
            description={error}
            onRetry={() => fetchOffers(lastQuery)}
          />
        </Card>
      ) : offers.length === 0 ? (
        <EmptyState
          icon={<FileText className="w-12 h-12" strokeWidth={1.75} />}
          title={
            searchTerm
              || filterCustomerId !== 'all'
              || filterStatus !== 'all'
              ? t('offers:empty.titleFiltered')
              : t('offers:empty.title')
          }
          description={
            searchTerm
              || filterCustomerId !== 'all'
              || filterStatus !== 'all'
              ? t('offers:empty.descriptionFiltered')
              : t('offers:empty.description')
          }
        />
      ) : (
        <div className="space-y-3">
          <Card noPadding>
            <OffersTable
              offers={offers}
              customerNames={customerNames}
              objectNames={objectNames}
              getStatusBadge={getStatusBadge}
              navigateToOffer={navigateToOffer}
              t={t}
            />
          </Card>

          <Pagination
            page={page}
            pageCount={pageCount}
            totalCount={totalCount}
            onPageChange={goToPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}

interface FiltersCardProps {
  searchTerm: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  filterCustomerId: string;
  onCustomerChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  filterStatus: OfferStatusFilter;
  onStatusChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  customers: { id?: string; name?: string | null }[];
  t: (key: string) => string;
}

function FiltersCard({
  searchTerm, onSearchChange,
  filterCustomerId, onCustomerChange,
  filterStatus, onStatusChange,
  customers, t,
}: FiltersCardProps) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
            <input
              type="text"
              placeholder={t('offers:placeholders.search')}
              value={searchTerm}
              onChange={onSearchChange}
              className="w-full pl-9 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={filterCustomerId}
            onChange={onCustomerChange}
            aria-label={t('offers:filters.customer')}
            className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          >
            <option value="all">
              {t('offers:filters.allCustomers')}
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={onStatusChange}
            aria-label={t('offers:filters.status')}
            className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          >
            <option value="all">{t('offers:filters.allStatus')}</option>
            <option value="draft">{t('offers:status.draft')}</option>
            <option value="sent">{t('offers:status.sent')}</option>
            <option value="accepted">{t('offers:status.accepted')}</option>
            <option value="rejected">{t('offers:status.rejected')}</option>
            <option value="expired">{t('offers:status.expired')}</option>
          </select>
        </div>
      </div>
    </div>
  );
}

interface OffersTableProps {
  offers: {
    id?: string | null;
    offerNumber?: string | null;
    customerId?: string | null;
    objectId?: string | null;
    contactPersonName?: string | null;
    contactSalutation?: string | null;
    status?: string | null;
    offerDate?: string | null;
    validUntilDate?: string | null;
    totalGrossAmount?: number | null;
  }[];
  customerNames: Record<string, string>;
  objectNames: Record<string, string>;
  getStatusBadge: (status?: string | null) => JSX.Element;
  navigateToOffer: (id?: string | null) => void;
  t: (key: string) => string;
}

function OffersTable({
  offers, customerNames, objectNames,
  getStatusBadge, navigateToOffer, t,
}: OffersTableProps) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>{t('offers:table.offerNumber')}</TableHeader>
          <TableHeader className="hidden md:table-cell">
            {t('offers:table.object')}
          </TableHeader>
          <TableHeader className="hidden lg:table-cell">
            {t('offers:table.contactPerson')}
          </TableHeader>
          <TableHeader>{t('offers:table.status')}</TableHeader>
          <TableHeader className="hidden sm:table-cell">
            {t('offers:table.date')}
          </TableHeader>
          <TableHeader className="hidden lg:table-cell">
            {t('offers:table.validUntil')}
          </TableHeader>
          <TableHeader>{t('offers:table.gross')}</TableHeader>
          <TableHeader>{t('common:actions.title')}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {offers.map((offer) => (
          <OfferRow
            key={offer.id}
            offer={offer}
            customerNames={customerNames}
            objectNames={objectNames}
            getStatusBadge={getStatusBadge}
            navigateToOffer={navigateToOffer}
          />
        ))}
      </TableBody>
    </Table>
  );
}

interface OfferRowProps {
  offer: OffersTableProps['offers'][number];
  customerNames: Record<string, string>;
  objectNames: Record<string, string>;
  getStatusBadge: (status?: string | null) => JSX.Element;
  navigateToOffer: (id?: string | null) => void;
}

function OfferRow({
  offer, customerNames, objectNames,
  getStatusBadge, navigateToOffer,
}: OfferRowProps) {
  const { t } = useTranslation(['common']);
  const handleClick = () => navigateToOffer(offer.id);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigateToOffer(offer.id);
    }
  };

  const cellProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    className: 'outline-none',
  };

  return (
    <TableRow>
      <TableCell className="cursor-pointer">
        <div {...cellProps}>
          <div className="font-mono-num text-[13px] font-medium text-slate-900">
            {offer.offerNumber || '\u2014'}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 sm:hidden">
            {customerNames[offer.customerId ?? ''] || '\u2014'}
          </div>
        </div>
      </TableCell>

      <TableCell className="hidden md:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 outline-none">
          {objectNames[offer.objectId ?? ''] || '\u2014'}
        </div>
      </TableCell>

      <TableCell className="hidden lg:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 outline-none">
          {formatContactPerson(toSalutation(offer.contactSalutation), offer.contactPersonName, t) || '\u2014'}
        </div>
      </TableCell>

      <TableCell className="cursor-pointer">
        <div {...cellProps}>
          {getStatusBadge(offer.status)}
        </div>
      </TableCell>

      <TableCell className="hidden sm:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 tabular-nums outline-none">
          {formatDate(offer.offerDate)}
        </div>
      </TableCell>

      <TableCell className="hidden lg:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 tabular-nums outline-none">
          {formatDate(offer.validUntilDate)}
        </div>
      </TableCell>

      <TableCell className="cursor-pointer text-right">
        <div {...cellProps} className="text-[13px] font-medium text-slate-900 tabular-nums outline-none">
          {formatMoney(offer.totalGrossAmount)}
        </div>
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToOffer(offer.id);
            }}
            title="Details anzeigen"
            aria-label="Details anzeigen"
            className="p-1.5"
          >
            <Eye className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
